export interface CatalogItem {
    name: string;
    description: string;
    slug: string;
    asset_count: number;
    color: string;
    icon: string;
    category: string;
    category_order: number;
    type: "reference" | "metric";
}

export interface CatalogPayload {
    items: CatalogItem[];
    total_asset_count: number;
}

// Defines enums for metric.slug + reference_data.slug values
// NOTE - these are hard-coded and must be kept in sync with the values in the database
export enum CatalogMetricSlugs {
    adjusted_daily_burns_dollar = "adjusted_daily_burns_dollar",
    adjusted_daily_burns_token = "adjusted_daily_burns_token",
    adjusted_daily_mints_dollar = "adjusted_daily_mints_dollar",
    adjusted_daily_mints_token = "adjusted_daily_mints_token",
    annually_active_addresses_count = "annually_active_addresses_count",
    apy_30_day = "apy_30_day",
    apy_7_day = "apy_7_day",
    bridged_token_market_cap_dollar = "bridged_token_market_cap_dollar",
    bridged_token_supply_token = "bridged_token_supply_token",
    bridged_token_value_dollar = "bridged_token_value_dollar",
    circulating_asset_value_dollar = "circulating_asset_value_dollar",
    circulating_market_value_dollar = "circulating_market_value_dollar",
    circulating_supply_token = "circulating_supply_token",
    cumulative_issued_amount_dollar = "cumulative_issued_amount_dollar",
    cumulative_issued_amount_token = "cumulative_issued_amount_token",
    daily_active_addresses_count = "daily_active_addresses_count",
    daily_burns_dollar = "daily_burns_dollar",
    daily_burns_token = "daily_burns_token",
    daily_mints_dollar = "daily_mints_dollar",
    daily_mints_token = "daily_mints_token",
    daily_transfer_volume_dollar = "daily_transfer_volume_dollar",
    daily_transfer_volume_token = "daily_transfer_volume_token",
    daily_yield_distributed_dollar = "daily_yield_distributed_dollar",
    daily_yield_rate = "daily_yield_rate",
    holding_addresses_count = "holding_addresses_count",
    hypothetical_10_000_performance = "hypothetical_10_000_performance",
    market_value_dollar = "market_value_dollar",
    monthly_active_addresses_count = "monthly_active_addresses_count",
    monthly_transfer_count = "monthly_transfer_count",
    monthly_transfer_volume = "monthly_transfer_volume",
    net_asset_value_dollar = "net_asset_value_dollar",
    number_of_daily_transactions_count = "number_of_daily_transactions_count",
    price_dollar = "price_dollar",
    quarterly_active_addresses_count = "quarterly_active_addresses_count",
    top_10_holder_concentration = "top_10_holder_concentration",
    top_5_holder_concentration = "top_5_holder_concentration",
    top_holder_concentration = "top_holder_concentration",
    total_asset_value_dollar = "total_asset_value_dollar",
    total_supply_token = "total_supply_token",
    trailing_1_month_return_percent = "trailing_1_month_return_percent",
    trailing_1_year_return_percent = "trailing_1_year_return_percent",
    trailing_30_day_active_addresses_count = "trailing_30_day_active_addresses_count",
    trailing_30_day_transfer_count = "trailing_30_day_transfer_count",
    trailing_30_day_transfer_volume = "trailing_30_day_transfer_volume",
    trailing_7_day_active_addresses_count = "trailing_7_day_active_addresses_count",
    trailing_7_day_transfer_count = "trailing_7_day_transfer_count",
    trailing_7_day_transfer_volume = "trailing_7_day_transfer_volume",
    weekly_active_addresses_count = "weekly_active_addresses_count",
    weekly_transfer_count = "weekly_transfer_count",
    weekly_transfer_volume = "weekly_transfer_volume",
}

export enum CatalogReferenceDataSlugs {
    type = "type",
    address = "address",
    annual_return_target_description = "annual_return_target_description",
    asset_class = "asset_class",
    asset_to_pegged_asset_ratio = "asset_to_pegged_asset_ratio",
    auditor = "auditor",
    primary_market__base_asset_ticker = "primary_market__base_asset_ticker",
    crypto_broker = "crypto_broker",
    crypto_custodian = "crypto_custodian",
    cusip = "cusip",
    decimals = "decimals",
    description = "description",
    dispute_resolution_country = "dispute_resolution_country",
    dispute_resolution_description = "dispute_resolution_description",
    distributes_income = "distributes_income",
    distribution_frequency_type = "distribution_frequency_type",
    domicile = "domicile",
    primary_market__investor_types = "primary_market__investor_types",
    exchange_agent = "exchange_agent",
    fee_structure_description = "fee_structure_description",
    fund_administrator = "fund_administrator",
    governing_body = "governing_body",
    holder_has_right_to_redeem = "holder_has_right_to_redeem",
    holder_rights_description = "holder_rights_description",
    icon_url = "icon_url",
    id = "id",
    inception_date = "inception_date",
    isin = "isin",
    primary_market__is_kyc_required = "primary_market__is_kyc_required",
    is_open_ended = "is_open_ended",
    issuer = "issuer",
    jurisdiction_country = "jurisdiction_country",
    primary_market__kyc_provider_id = "primary_market__kyc_provider_id",
    lockup_description = "lockup_description",
    lockup_period_months = "lockup_period_months",
    total_management_fee_bps = "total_management_fee_bps",
    manager = "manager",
    primary_market__minimum_subscription_amt = "primary_market__minimum_subscription_amt",
    name = "name",
    network = "network",
    other_fees_description = "other_fees_description",
    paying_agent = "paying_agent",
    pegged_asset = "pegged_asset",
    peg_maintenance_description = "peg_maintenance_description",
    total_performance_fee_bps = "total_performance_fee_bps",
    platform = "platform",
    primary_market = "primary_market",
    primary_market__redemption_description = "primary_market__redemption_description",
    primary_market__redemption_fee_bps = "primary_market__redemption_fee_bps",
    primary_market__redemption_frequency = "primary_market__redemption_frequency",
    region_ids = "region_ids",
    regulatory_framework = "regulatory_framework",
    sector_ids = "sector_ids",
    standards = "standards",
    sub_advisor = "sub_advisor",
    primary_market__subscription_description = "primary_market__subscription_description",
    primary_market__subscription_fee_bps = "primary_market__subscription_fee_bps",
    primary_market__subscription_frequency = "primary_market__subscription_frequency",
    term_description = "term_description",
    term_length_years = "term_length_years",
    tokenization_sponsorship_type = "tokenization_sponsorship_type",
    ticker = "ticker",
    tokenization_type = "tokenization_type",
    traditional_broker = "traditional_broker",
    traditional_custodian = "traditional_custodian",
    transferability_description = "transferability_description",
    transferability_type = "transferability_type",
    transfer_agent = "transfer_agent",
    website = "website",
}
